import React from "react";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error | null;
}

class ErrorBoundary extends React.Component<React.PropsWithChildren, ErrorBoundaryState> {
  constructor(props: React.PropsWithChildren) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Enhanced logging with better error categorization
    console.error("App crashed:", error, info);
    
    // Check if it's a dynamic import error and handle gracefully
    const isDynamicImportError = /Failed to fetch dynamically imported module|Loading chunk|Importing a module script failed/i.test(error.message);
    if (isDynamicImportError) {
      console.warn('Dynamic import error detected, attempting recovery...');
      // Clear any cached modules and reload
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }

  render() {
    if (this.state.hasError) {
      // Check if it's a dynamic import error
      const isDynamicImportError = this.state.error && 
        /Failed to fetch dynamically imported module|Loading chunk|Importing a module script failed/i.test(this.state.error.message);
      
      if (isDynamicImportError) {
        // Show a cleaner error for dynamic import issues
        return (
          <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-purple-50 to-pink-50">
            <div className="max-w-md w-full rounded-xl shadow-xl p-8 bg-white border border-purple-100">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <h1 className="text-xl font-bold mb-2 text-gray-900">Loading Issue</h1>
                <p className="text-sm text-gray-600 mb-6">We're having trouble loading this page. This usually resolves with a quick refresh.</p>
                <button
                  onClick={() => window.location.reload()}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors w-full"
                >
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        );
      }
      
      // Generic error fallback
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-red-50 to-orange-50">
          <div className="max-w-md w-full rounded-xl shadow-xl p-8 bg-white border border-red-100">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.072 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h1 className="text-xl font-bold mb-2 text-gray-900">Something went wrong</h1>
              <p className="text-sm text-gray-600 mb-6">An unexpected error occurred. Please try refreshing the page.</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors w-full"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children as React.ReactElement;
  }
}

export default ErrorBoundary;
